<template>
  <adn-anchor-navigation :anchors="anchors" :style="cssProps" class="product-navigation" />
</template>

<script lang="ts">
import { useProduct } from '@b2ag/catalog/src/useProduct'
import { computed, onMounted, onUnmounted, ref, toRefs, defineComponent } from 'vue'
import { i18n } from '@b2ag/locale'
import { buildCallerId } from '@b2ag/utils/src/caller-id'
import { useAuthService } from '@b2ag/auth'
import { useRealtimeStocks } from '@b2ag/realtime-stocks/src/useRealtimeStocks'
import AdnAnchorNavigation from '@b2ag/design-system/src/components/AdnAnchorNavigation/AdnAnchorNavigation.vue'

import type { Anchor } from './typings'

export default defineComponent({
  name: 'PProductMenu',
  props: {
    productId: {
      type: String,
      required: true,
    },
    cooperativeName: String,
    offerListTarget: String,
    stocksTarget: String,
    productDetailTarget: String,
    productCooperativeReviewTarget: String,
    productDocumentationTarget: String,
    productUsageTarget: String,
    calculatorTarget: String,
  },
  components: {
    AdnAnchorNavigation,
  },
  setup(props) {
    const {
      productUsageTarget,
      calculatorTarget,
      cooperativeName,
      productCooperativeReviewTarget,
      productDocumentationTarget,
      productDetailTarget,
      offerListTarget,
      stocksTarget,
    } = toRefs(props)
    const { isSellable, hasDetail, hasDocumentation, hasCooperativeReview, isCalculableProduct, hasUsages, product } =
      useProduct(props.productId)
    const scrollBarWidth = ref(0)
    const onResize = () => {
      scrollBarWidth.value = window.innerWidth - document.body.clientWidth
    }

    const { isRealtimeStocksEnabled } = useRealtimeStocks(product.value!.variants, {
      callerId: buildCallerId(),
      accessTokenSource: () => useAuthService().getAccessToken(),
    })

    onMounted(() => {
      onResize()
      window.addEventListener('resize', onResize, { passive: true })
    })
    onUnmounted(() => {
      window.removeEventListener('resize', onResize)
    })

    const cssProps = computed(() => ({
      '--scroolbar-width': `${scrollBarWidth.value}px`,
    }))
    const anchors = computed(() => {
      const result: Anchor[] = []

      if (isRealtimeStocksEnabled.value) {
        result.push({
          label: i18n._('Stocks'),
          selector: stocksTarget.value,
        })
      }
      if (isSellable) {
        result.push({
          label: i18n._('Prix'),
          selector: offerListTarget.value,
        })
      }
      if (isCalculableProduct) {
        result.push({
          label: i18n._('Calculatrice'),
          selector: calculatorTarget.value,
        })
      }
      if (hasDetail) {
        result.push({
          label: i18n._('Détails du produit'),
          selector: productDetailTarget.value,
        })
      }
      if (hasUsages) {
        result.push({
          label: i18n._('Usages'),
          selector: productUsageTarget.value,
        })
      }
      if (hasCooperativeReview) {
        result.push({
          label: i18n.t(`COOPERATIVE_REVIEW`, { cooperativeName: cooperativeName.value }),
          selector: productCooperativeReviewTarget.value,
        })
      }
      if (hasDocumentation) {
        result.push({
          label: i18n._('Documentation'),
          selector: productDocumentationTarget.value,
        })
      }
      return result
    })

    return {
      isRealtimeStocksEnabled,
      isSellable,
      hasDetail,
      hasDocumentation,
      hasCooperativeReview,
      hasUsages,
      isCalculableProduct,
      cssProps,
      anchors,
    }
  },
})
</script>

<style lang="scss">
.product-navigation {
  max-width: calc(100vw - var(--scroolbar-width));
  margin: 0 calc(-50vw + 50%) 0 calc(-50vw + 50% + var(--scroolbar-width) / 2);

  --item-height: 44px;

  background-color: var(--color-background);
  transition: all 0.2s ease-out;

  &.stuck {
    box-shadow: 0 2px 2px -2px rgb(0 0 0 / 25%);
  }

  top: var(--mobile-header-height); // $mobile-header-height;

  @media (min-width: $breakpoint-sm) {
    --item-height: 54px;

    top: $tablet-header-height;
  }

  @media (min-width: $breakpoint-lg) {
    top: var(--desktop-header-height);
  }
}

.content--menu-open .product-navigation {
  @media (min-width: $breakpoint-lg) {
    max-width: calc(100vw - var(--scroolbar-width) - $desktop-sidemenu-width);
    margin: 0 calc(-50vw + 50%) 0 calc(-50vw + 50% + var(--scroolbar-width) / 2 + $desktop-sidemenu-width / 2);
  }
}
</style>
